import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

const BASE_LAYER_ID = '__base__';

export interface HeaderTitle {
    icon?: ReactNode;
    title: string;
    subtitle?: string;
    badges?: ReactNode;
    /** Richer header icon treatment (color depth, saturation, shadow). */
    intense?: boolean;
}

interface HeaderLayer {
    action?: ReactNode;
    center?: ReactNode;
    versionSelector?: ReactNode;
    title?: HeaderTitle | null;
}

interface HeaderActionContextValue {
    headerAction: ReactNode;
    headerCenter: ReactNode;
    headerVersionSelector: ReactNode;
    headerTitle: HeaderTitle | null;
    /** Backward-compat: writes to the __base__ layer's action slot. */
    setHeaderAction: (action: ReactNode) => void;
    /** Backward-compat: writes to the __base__ layer's center slot. */
    setHeaderCenter: (node: ReactNode) => void;
    /** Backward-compat: writes to the __base__ layer's versionSelector slot. */
    setHeaderVersionSelector: (node: ReactNode) => void;
    /** Writes to the __base__ layer's title slot. */
    setHeaderTitle: (title: HeaderTitle | null) => void;
    /** Push (or update) a named layer. The topmost layer wins for each slot. */
    pushHeaderLayer: (id: string, layer: HeaderLayer) => void;
    /** Remove a named layer. The next layer down becomes active. */
    removeHeaderLayer: (id: string) => void;
}

const HeaderActionContext = createContext<HeaderActionContextValue | null>(null);

function resolveSlot(
    layerOrder: string[],
    layers: Map<string, HeaderLayer>,
    slot: keyof HeaderLayer,
): ReactNode {
    for (let i = layerOrder.length - 1; i >= 0; i--) {
        const layer = layers.get(layerOrder[i]);
        if (layer && layer[slot] !== undefined && layer[slot] !== null) {
            return layer[slot] as ReactNode;
        }
    }
    return null;
}

function resolveTitle(
    layerOrder: string[],
    layers: Map<string, HeaderLayer>,
): HeaderTitle | null {
    for (let i = layerOrder.length - 1; i >= 0; i--) {
        const layer = layers.get(layerOrder[i]);
        if (layer?.title !== undefined && layer.title !== null) {
            return layer.title;
        }
    }
    return null;
}

export function HeaderActionProvider({ children }: { children: ReactNode }) {
    const [layers, setLayers] = useState<Map<string, HeaderLayer>>(() => new Map([[BASE_LAYER_ID, {}]]));
    const [layerOrder, setLayerOrder] = useState<string[]>([BASE_LAYER_ID]);

    const pushHeaderLayer = useCallback((id: string, layer: HeaderLayer) => {
        setLayers((prev) => {
            const next = new Map(prev);
            const existing = next.get(id);
            const merged = existing ? { ...existing, ...layer } : layer;
            if (
                existing &&
                existing.action === merged.action &&
                existing.center === merged.center &&
                existing.versionSelector === merged.versionSelector &&
                existing.title === merged.title
            ) {
                return prev;
            }
            next.set(id, merged);
            return next;
        });
        setLayerOrder((prev) => {
            const without = prev.filter((x) => x !== id);
            const next = [...without, id];
            if (prev.length === next.length && prev.every((v, i) => v === next[i])) {
                return prev;
            }
            return next;
        });
    }, []);

    const removeHeaderLayer = useCallback((id: string) => {
        if (id === BASE_LAYER_ID) return;
        setLayers((prev) => {
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
        setLayerOrder((prev) => prev.filter((x) => x !== id));
    }, []);

    const setHeaderAction = useCallback((action: ReactNode) => {
        setLayers((prev) => {
            const base = prev.get(BASE_LAYER_ID) ?? {};
            if (base.action === action) return prev;
            const next = new Map(prev);
            next.set(BASE_LAYER_ID, { ...base, action });
            return next;
        });
    }, []);

    const setHeaderCenter = useCallback((center: ReactNode) => {
        setLayers((prev) => {
            const base = prev.get(BASE_LAYER_ID) ?? {};
            if (base.center === center) return prev;
            const next = new Map(prev);
            next.set(BASE_LAYER_ID, { ...base, center });
            return next;
        });
    }, []);

    const setHeaderVersionSelector = useCallback((versionSelector: ReactNode) => {
        setLayers((prev) => {
            const base = prev.get(BASE_LAYER_ID) ?? {};
            if (base.versionSelector === versionSelector) return prev;
            const next = new Map(prev);
            next.set(BASE_LAYER_ID, { ...base, versionSelector });
            return next;
        });
    }, []);

    const setHeaderTitle = useCallback((title: HeaderTitle | null) => {
        setLayers((prev) => {
            const base = prev.get(BASE_LAYER_ID) ?? {};
            if (base.title === title) return prev;
            const next = new Map(prev);
            next.set(BASE_LAYER_ID, { ...base, title });
            return next;
        });
    }, []);

    const headerAction = useMemo(() => resolveSlot(layerOrder, layers, 'action'), [layerOrder, layers]);
    const headerCenter = useMemo(() => resolveSlot(layerOrder, layers, 'center'), [layerOrder, layers]);
    const headerVersionSelector = useMemo(() => resolveSlot(layerOrder, layers, 'versionSelector'), [layerOrder, layers]);
    const headerTitle = useMemo(() => resolveTitle(layerOrder, layers), [layerOrder, layers]);

    const value = useMemo<HeaderActionContextValue>(
        () => ({
            headerAction,
            headerCenter,
            headerVersionSelector,
            headerTitle,
            setHeaderAction,
            setHeaderCenter,
            setHeaderVersionSelector,
            setHeaderTitle,
            pushHeaderLayer,
            removeHeaderLayer,
        }),
        [headerAction, headerCenter, headerVersionSelector, headerTitle, setHeaderAction, setHeaderCenter, setHeaderVersionSelector, setHeaderTitle, pushHeaderLayer, removeHeaderLayer],
    );

    return (
        <HeaderActionContext.Provider value={value}>
            {children}
        </HeaderActionContext.Provider>
    );
}

const NOOP_HEADER = {
    headerAction: null,
    setHeaderAction: () => {},
    headerCenter: null,
    setHeaderCenter: () => {},
    headerVersionSelector: null,
    setHeaderVersionSelector: () => {},
    headerTitle: null,
    setHeaderTitle: () => {},
    pushHeaderLayer: () => {},
    removeHeaderLayer: () => {},
};

export function useHeaderAction() {
    const ctx = useContext(HeaderActionContext);
    if (!ctx) return NOOP_HEADER;
    return ctx;
}
